import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { transactionActionBulk } from '../services/WalletTransactionServices';
import '../../../../../styles/components/action-modal.scss';

export class ConfirmationModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      rejectReason: '',
      showRejectInput: true,
      rejectError: '',
      processingResults: null,
    };
  }

  componentDidUpdate(prevProps) {
    // Reset state when modal opens/closes or action changes
    if (!prevProps.show && this.props.show) {
      this.resetState();
    }

    // Auto-show reject input if confirmAction is 'reject'
    if (this.props.show && this.props.confirmAction === 'reject' && !this.state.showRejectInput) {
      this.setState({ showRejectInput: true });
    }
  }

  resetState = () => {
    this.setState({
      submitting: false,
      rejectReason: '',
      showRejectInput: this.props.confirmAction === 'reject',
      rejectError: '',
      processingResults: null,
    });
  };

  handleBulkApprove = async () => {
    const { selectedIds, onComplete } = this.props;

    if (!selectedIds || selectedIds.length === 0) {
      toast.error('No payments selected');
      return;
    }

    try {
      this.setState({ submitting: true, processingResults: null });

      const payload = {
        ids: selectedIds, // Send as array for bulk operation
        action: 'approve',
      };

      const response = await transactionActionBulk(payload);
      if (response.success) {
        const failedItems = (response.data || []).filter((item) => item.status !== 'success');
        const successItems = (response.data || []).filter((item) => item.status === 'success');

        if (failedItems.length > 0 && successItems.length > 0) {
          toast.warning(
            `Some payments failed (${failedItems.length}) and ${successItems.length} succeeded`
          );
          this.setState({
            processingResults: {
              success: false,
              message: `${failedItems.length} failed: ${failedItems
                .map((f) => f.message)
                .join(', ')}`,
              details: failedItems,
            },
          });
        } else if (failedItems.length > 0) {
          toast.error(`All ${failedItems.length} payments failed`);
          this.setState({
            processingResults: {
              success: false,
              message: failedItems.map((f) => f.message).join(', '),
              details: failedItems,
            },
          });
        } else {
          const count = successItems.length;
          toast.success(
            response.message || `${count} payment${count > 1 ? 's' : ''} approved successfully`
          );
          onComplete?.();
          this.handleClose();
        }
      } else {
        toast.error(response.message || 'Failed to approve payments');
        this.setState({
          processingResults: {
            success: false,
            message: response.message,
          },
        });
      }
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(error.message || 'Something went wrong during approval');
      this.setState({
        processingResults: {
          success: false,
          message: 'Network error occurred',
        },
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleBulkReject = async () => {
    const { selectedIds, onComplete } = this.props;
    const { rejectReason } = this.state;

    // Validation
    if (!rejectReason?.trim()) {
      this.setState({ rejectError: 'Rejection reason is required' });
      return;
    }

    if (rejectReason.trim().length < 10) {
      this.setState({ rejectError: 'Reason must be at least 10 characters' });
      return;
    }

    if (!selectedIds || selectedIds.length === 0) {
      toast.error('No payments selected');
      return;
    }

    try {
      this.setState({ submitting: true, rejectError: '', processingResults: null });

      const payload = {
        ids: selectedIds, // Send as array for bulk operation
        action: 'reject',
        reason: rejectReason.trim(),
      };

      const response = await transactionActionBulk(payload);

      if (response.success) {
        const count = selectedIds.length;
        toast.success(
          response.message || `${count} payment${count > 1 ? 's' : ''} rejected successfully`
        );
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response.message || 'Failed to reject payments');
        this.setState({
          processingResults: {
            success: false,
            message: response.message,
          },
        });
      }
    } catch (error) {
      console.error('Rejection error:', error);
      toast.error(error.message || 'Something went wrong during rejection');
      this.setState({
        processingResults: {
          success: false,
          message: 'Network error occurred',
        },
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleRejectClick = () => {
    this.setState({ showRejectInput: true, rejectError: '' });
  };

  handleBackFromReject = () => {
    this.setState({
      showRejectInput: false,
      rejectReason: '',
      rejectError: '',
      processingResults: null,
    });
  };

  handleClose = () => {
    if (this.state.submitting) return; // Prevent closing while submitting
    this.resetState();
    this.props.onClose?.();
  };

  handleReasonChange = (e) => {
    const value = e.target.value;
    this.setState({
      rejectReason: value,
      rejectError: value.trim() ? '' : 'Rejection reason is required',
    });
  };

  render() {
    const { show, selectedIds, selectedSum, confirmAction } = this.props;
    const { rejectReason, submitting, showRejectInput, rejectError, processingResults } =
      this.state;

    // Don't render if no payments selected
    if (!selectedIds || selectedIds.length === 0) return null;

    const paymentCount = selectedIds.length;
    const isMultiple = paymentCount > 1;
    const modalTitle = isMultiple
      ? `Bulk Payment Action (${paymentCount} Payments)`
      : 'Payment Action Required';

    return (
      <CustomModal
        isOpen={show}
        header={modalTitle}
        onClose={this.handleClose}
        closeOnOverlayClick={!submitting}
      >
        <div className="action-modal-container">
          {/* Payment Summary Section */}
          <div className="payment-details-card">
            <h6>{isMultiple ? 'Selected Payments Summary' : 'Payment Details'}</h6>

            <div className="details-grid">
              <div className="detail-item-new">
                <span className="detail-label">{isMultiple ? 'Total Payments' : 'Payment ID'}</span>
                <span className="detail-value">{isMultiple ? paymentCount : selectedIds[0]}</span>
              </div>

              <div className="detail-item-new">
                <span className="detail-label">Total Amount</span>
                <span className="detail-value amount">
                  {parseFloat(selectedSum || 0).toFixed(3)} OMR
                </span>
              </div>
            </div>
          </div>

          {/* Reject Reason Section */}
          {showRejectInput && (
            <div className="reject-section">
              <label htmlFor="rejectReason" className="reject-label">
                Reason for Rejection <span className="required">*</span>
              </label>
              <textarea
                id="rejectReason"
                className={`reject-textarea ${rejectError ? 'error' : ''}`}
                value={rejectReason}
                onChange={this.handleReasonChange}
                placeholder={`Enter a detailed reason for rejecting ${
                  isMultiple ? 'these payments' : 'this payment'
                }... (minimum 10 characters)`}
                rows={4}
                disabled={submitting}
                maxLength={500}
              />
              <div className="textarea-footer">
                {rejectError ? (
                  <span className="error-message">{rejectError}</span>
                ) : (
                  <span className="char-count">{rejectReason.length}/500</span>
                )}
              </div>
            </div>
          )}

          {/* Warning for operations */}
          <div className="bulk-warning">
            <AlertCircle size={16} />
            <span>
              You are about to {confirmAction === 'reject' ? 'reject' : 'approve'} {paymentCount}{' '}
              payments. This action cannot be undone.
            </span>
          </div>

          {/* Processing Results */}
          {processingResults && !processingResults.success && (
            <div className="error-alert">
              <AlertCircle size={18} />
              <div>
                <strong>Some payments failed:</strong>
                <ul>
                  {processingResults.details?.map((item) => (
                    <li key={item.id}>
                      ID {item.id}: {item.message || item.status}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons-container">
            <>
              <button
                className="modal-btn btn-cancel"
                onClick={this.handleClose}
                disabled={submitting}
              >
                Cancel
              </button>

              {/* Only show Approve button if confirmAction === 'approve' */}
              {confirmAction === 'approve' && (
                <button
                  className="modal-btn btn-confirm"
                  onClick={this.handleBulkApprove}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-small"></span>
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Approve {isMultiple ? `(${paymentCount})` : ''}
                    </>
                  )}
                </button>
              )}

              {/* Only show Reject button if confirmAction === 'reject' */}
              {confirmAction === 'reject' && (
                <button
                  className="modal-btn btn-danger"
                  onClick={this.handleBulkReject}
                  disabled={submitting || !rejectReason.trim() || rejectReason.trim().length < 10}
                >
                  <XCircle size={18} />
                  Reject {isMultiple ? `(${paymentCount})` : ''}
                </button>
              )}
            </>
          </div>
        </div>
      </CustomModal>
    );
  }
}
